import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStationDetailsUrl, paths } from '../../../routes';
import { tenantSelector } from '../../../selector/authenticationSelector';
import type { RootState } from '../../../store/store';
import { ROOM_PRODUCT_USE_CASES } from '../../services/serviceConfigurationRules';
import PageHeader from '../../../components/PageHeader';
import PageHeadline from '../../../components/PageHeadline';
import CustomButton from '../../../components/CustomButton';
import Stepper from '../../../components/Stepper';
import CustomModal from '../../common/CustomModal';
import {
    type StationCreationForm,
    type WizardVariant,
    makeInitialStationForm,
} from './stationCreationForm';
import { houseStepError, stationStepError } from './stationCreationValidation';
import {
    buildCreateHouseRequest,
    buildCreateObjectsRequest,
    buildCreateStationRequest,
} from './stationCreationPayload';
import {
    invalidateInventoryReaders,
    useCreateStationMutation,
    useCreateStationObjectsMutation,
} from '../../../api/inventory/inventoryApiSlice';
import {
    type InventoryFailure,
    readInventoryFailure,
} from '../../../api/inventory/inventoryErrors';
import InventoryErrorPanel from './components/InventoryErrorPanel';
import LocationStep from './components/LocationStep';
import StationStep from './components/StationStep';
import ObjectsStep from './components/ObjectsStep';
import LinkProductStep from './components/LinkProductStep';
import HouseStep from './components/HouseStep';
import { clearStationDraft, readStationDraft, saveStationDraft } from './stationDraft';

type WizardStep = 0 | 1 | 2 | 3;
const LAST_STEP: Record<WizardVariant, WizardStep> = { station: 3, house: 1 };

/**
 * Object & station creation wizard (asset side — architecture §9a). Built
 * against the target four-entity contract (`api/station/stationApiTypes.ts`)
 * and now WIRED to it: the endpoints exist under `/admin/inventory` (#2134,
 * merged in #2225). Submit-path (b) is complete, so the route is in the
 * sidebar, gated on the manage-assets permission.
 *
 * The sequence is station → objects, not station → objects → link: the objects
 * call takes `productId` and creates the junction rows in the same transaction
 * as the assets, so there is no window where objects exist but fulfil nothing.
 */
type UpdateField = <K extends keyof StationCreationForm>(
    key: K, value: StationCreationForm[K],
) => void;

/** Which flow this is, said on the screen rather than assumed. */
const ScopeHint = ({ isHouse }: { isHouse: boolean }) => {
    const { t } = useTranslation();
    return (
        <p className={'mb-5 rounded-lg bg-foreground/5 px-4 py-3 text-sm text-foreground/60'}>
            {isHouse ? t('house_create.scope_hint') : (
                <>
                    {t('station_create.scope_hint')}{' '}
                    <Link to={paths.createAsset} className={'text-primary hover:underline'}>
                        {t('station_create.scope_hint_link')}
                    </Link>
                </>
            )}
        </p>
    );
};

/** The variant's steps, all mounted so their state survives navigation. */
const WizardSteps = ({ variant, currentStep, form, update }: {
    variant: WizardVariant;
    currentStep: WizardStep;
    form: StationCreationForm;
    update: UpdateField;
}) => {
    const shown = (step: WizardStep) => ({ display: currentStep === step ? 'block' : 'none' });
    return (
        <>
            <div style={shown(0)}>
                <LocationStep form={form} update={update} variant={variant} />
            </div>
            {variant === 'house' ? (
                <div style={shown(1)}>
                    <HouseStep form={form} update={update} />
                </div>
            ) : (
                <>
                    <div style={shown(1)}><StationStep form={form} update={update} /></div>
                    <div style={shown(2)}><ObjectsStep form={form} update={update} /></div>
                    <div style={shown(3)}><LinkProductStep form={form} update={update} /></div>
                </>
            )}
        </>
    );
};

const CreateStationPage = ({ variant = 'station' }: { variant?: WizardVariant }) => {
    const { t } = useTranslation();
    const isHouse = variant === 'house';
    const lastStep = LAST_STEP[variant];
    // A house's new location is scoped to the tenant's room use cases — the
    // backend accepts nothing else for a house (`houseUseCasesError`).
    const services = useSelector((state: RootState) => state.authentication.config?.services);
    const roomUseCases = useMemo(
        () => (Array.isArray(services) ? services : [])
            .filter((service: string) => ROOM_PRODUCT_USE_CASES.has(service)),
        [services],
    );
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const [currentStep, setCurrentStep] = useState<WizardStep>(0);
    const [form, setForm] = useState<StationCreationForm>(makeInitialStationForm);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [failure, setFailure] = useState<InventoryFailure | null>(null);
    /**
     * Set when the station was created but its objects were not.
     *
     * The objects call is transactional per batch, so this is the one partial
     * state the sequence can leave: a real, empty station exists. Naming it —
     * with its id — is the difference between "retry and get a second empty
     * station" and knowing what is already there.
     */
    const [orphanStationId, setOrphanStationId] = useState<string | null>(null);

    const [createStation, { isLoading: isCreatingStation }] = useCreateStationMutation();
    const [createObjects, { isLoading: isCreatingObjects }] = useCreateStationObjectsMutation();
    const isSubmitting = isCreatingStation || isCreatingObjects;

    // Reset the wizard when the active tenant changes (CLAUDE.md "Reset State on
    // Tenant / Context Switch") — a master switching tenants must not carry the
    // previous tenant's entries. Guard against the same-value echo of the lagging
    // mirror by tracking the previous tenant id.
    const tenant = useSelector(tenantSelector);
    const prevTenant = useRef(tenant);
    useEffect(() => {
        if (prevTenant.current !== tenant) {
            prevTenant.current = tenant;
            setForm(makeInitialStationForm());
            setCurrentStep(0);
            setFailure(null);
            setOrphanStationId(null);
        }
    }, [tenant]);

    /**
     * Restore a draft once, for the tenant on screen.
     *
     * Separate from the tenant-switch reset above and guarded by its own ref:
     * two effects writing the same setter is the race CLAUDE.md documents, so
     * this one only ever runs on the first render that has a tenant.
     */
    const restored = useRef(false);
    useEffect(() => {
        if (restored.current || !tenant) return;
        restored.current = true;
        const draft = readStationDraft(tenant, variant);
        if (draft) {
            setForm(draft.form);
            setCurrentStep(Math.min(Math.max(draft.step, 0), lastStep) as WizardStep);
        }
    }, [tenant, variant, lastStep]);

    // Persist as they type, so a reload costs nothing. (It is no longer needed
    // to see a Product created in the other tab — `LinkProductStep` refetches on
    // focus — but a reload still happens for plenty of other reasons.)
    useEffect(() => {
        if (!restored.current) return;
        saveStationDraft(tenant, form, currentStep, variant);
    }, [tenant, form, currentStep, variant]);

    const stepLabels = isHouse
        ? [t('station_create.steps.location'), t('house_create.steps.house')]
        : [
            t('station_create.steps.location'),
            t('station_create.steps.station'),
            t('station_create.steps.objects'),
            t('station_create.steps.link'),
        ];

    const update = useCallback(
        <K extends keyof StationCreationForm>(key: K, value: StationCreationForm[K]) => {
            setForm((prev) => ({ ...prev, [key]: value }));
        },
        [],
    );

    /*
     * Why the step's reason, and not only whether it is valid.
     *
     * `stationStepError` has always returned an i18n key per failing step, and
     * nothing ever rendered one: the wizard just disabled the button, so every
     * `station_create.validation.*` string was dead and a blocked step showed
     * no reason at all. That was survivable while step 3 could fail exactly one
     * way — with no Product picked, the empty select said it for us.
     *
     * With several Products per station (#2280) it can fail four ways, three of
     * them invisible in the form: a duplicate Product, a missing count, and
     * counts that do not total the positions. A disabled button with no message
     * is a failure that looks like an absence, so the reason goes on screen.
     */
    const stepError = useMemo(
        () => (isHouse ? houseStepError : stationStepError)(currentStep, form),
        [isHouse, currentStep, form],
    );
    const stepValid = stepError === null;

    const handleNext = useCallback(() => {
        setCurrentStep((prev) => Math.min(prev + 1, lastStep) as WizardStep);
    }, [lastStep]);

    const handleBack = useCallback(() => {
        setCurrentStep((prev) => Math.max(prev - 1, 0) as WizardStep);
    }, []);

    /**
     * Create the station, then its objects — in that order, because the objects
     * need a station id.
     *
     * The link to the Product rides on the objects call rather than a third
     * request: the controller creates the `product_resource_link` rows inside
     * the same transaction as the assets, so there is no window in which the
     * objects exist but fulfil nothing. A separate `POST /product-links` could
     * fail after the objects were written and leave exactly that.
     *
     * If the objects call fails, the station is already committed. That is
     * reported with its id rather than silently retried — retrying the whole
     * sequence would create a second empty station.
     */
    /**
     * A house is ONE call: it has no objects to create afterwards, so there is
     * no orphan state. It lands on its own page, because an empty house has
     * no object to appear under in the objects overview yet.
     */
    const handleSubmitHouse = useCallback(async () => {
        setFailure(null);
        try {
            const house = await createStation(buildCreateHouseRequest(form, roomUseCases)).unwrap();
            clearStationDraft(tenant, variant);
            invalidateInventoryReaders(dispatch);
            navigate(makeStationDetailsUrl(house.id));
        } catch (error) {
            setFailure(readInventoryFailure(error));
        }
    }, [createStation, dispatch, form, navigate, roomUseCases, tenant, variant]);

    const handleSubmit = useCallback(async () => {
        setFailure(null);
        setOrphanStationId(null);

        let stationId: string;
        try {
            const station = await createStation(buildCreateStationRequest(form)).unwrap();
            stationId = station.id;
        } catch (error) {
            setFailure(readInventoryFailure(error));
            return;
        }

        try {
            await createObjects(buildCreateObjectsRequest(form, stationId)).unwrap();
        } catch (error) {
            setFailure(readInventoryFailure(error));
            setOrphanStationId(stationId);
            return;
        }

        clearStationDraft(tenant);
        invalidateInventoryReaders(dispatch);
        // The objects overview, not Zugangsverwaltung: what was just created is
        // inventory. `/depots` is the access-management surface, which shows
        // the upsell page for a tenant without the add-on and never the new
        // station — landing there made a successful creation look like a
        // failure.
        navigate(paths.assets);
    }, [createObjects, createStation, dispatch, form, navigate, tenant]);

    return (
        <div className={'mx-auto max-w-[1000px] p-4'}>
            <PageHeader>
                <PageHeadline>
                    {isHouse ? t('house_create.title') : t('station_create.title')}
                </PageHeadline>
                <CustomButton
                    title={t('station_create.buttons.cancel')}
                    onClick={() => setShowCancelDialog(true)}
                    isFilled={false}
                />
            </PageHeader>

            {/* Which flow this is, said on the screen rather than assumed.
                A venue or guest apartment is one bookable thing that is also
                its own station, and it keeps the richer single-listing form —
                so a room-renting tenant who lands here should be told to go
                back rather than build a structurally wrong station. Co-living
                is the exception and genuinely belongs here: one flat as a
                station, its rooms as objects. */}
            <ScopeHint isHouse={isHouse} />

            <div className={'mb-5'}>
                <Stepper steps={stepLabels} currentStep={currentStep} />
            </div>

            {/* All steps stay mounted to preserve state across navigation. */}
            <div className={'min-h-[360px]'}>
                <WizardSteps
                    variant={variant}
                    currentStep={currentStep}
                    form={form}
                    update={update}
                />
            </div>

            {(failure || orphanStationId) && (
                <InventoryErrorPanel failure={failure} orphanStationId={orphanStationId} />
            )}

            {/* The reason the next/create button is disabled.
                Shown on every step, not only step 3: these strings were written
                as instructions ("Bitte gib einen Standortnamen ein"), so they
                read as guidance for what the step still needs rather than as a
                reprimand — and a wizard that explains one step and stays mute
                on the other three is worse than one that explains none. */}
            {stepError && (
                <p className={'mt-4 text-sm text-red-500'}>{t(stepError)}</p>
            )}

            <div className={'mt-5 flex items-center justify-between border-t border-foreground/10 pt-4'}>
                <CustomButton
                    title={t('station_create.buttons.back')}
                    onClick={handleBack}
                    isFilled={false}
                    disabled={currentStep === 0}
                />
                {currentStep < lastStep ? (
                    <CustomButton
                        title={t('station_create.buttons.next')}
                        onClick={handleNext}
                        disabled={!stepValid}
                    />
                ) : (
                    <CustomButton
                        title={isHouse ? t('house_create.buttons.create') : t('station_create.buttons.create')}
                        onClick={isHouse ? handleSubmitHouse : handleSubmit}
                        disabled={!stepValid || isSubmitting}
                        loading={isSubmitting}
                    />
                )}
            </div>

            <CustomModal
                open={showCancelDialog}
                onClose={() => setShowCancelDialog(false)}
                title={t('station_create.cancel_dialog.title')}
                description={t('station_create.cancel_dialog.description')}
                saveButton={
                    <CustomButton
                        title={t('station_create.buttons.yes_cancel')}
                        onClick={() => {
                            // Cancelling is a decision to discard, so the draft
                            // goes with it — otherwise the next visit silently
                            // reopens what the operator just abandoned.
                            clearStationDraft(tenant, variant);
                            navigate(paths.assets);
                        }}
                        variant={'delete'}
                    />
                }
                closeButton={
                    <CustomButton
                        title={t('station_create.buttons.continue_editing')}
                        onClick={() => setShowCancelDialog(false)}
                        isFilled={false}
                    />
                }
            />
        </div>
    );
};

export default CreateStationPage;
